import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Menu,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import {
  adminEndpoints,
  MessageEntry,
  MessageInput,
  MessagesResponse,
  MessageStatus,
  MESSAGE_STATUSES,
  getLanguage,
  getScreenIdsFromMessages,
  getMessagesForScreen,
  getStatusesFromMessages,
  filterMessagesByStatus,
  formatScreenLabel,
} from '../services/api';

const Messages: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showError } = useError();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [messagesData, setMessagesData] = useState<MessagesResponse | null>(null);
  const [selectedScreen, setSelectedScreen] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<MessageInput>({ message: '', status: 'neutral' });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [statusMenuTarget, setStatusMenuTarget] = useState<'edit' | 'create'>('edit');
  const [selectedLanguage] = useState<string>(getLanguage());

  const [createForm, setCreateForm] = useState<MessageInput>({ message: '', status: 'neutral' });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminEndpoints.getMessages();
      if (response.data) {
        const data = response.data as MessagesResponse;
        setMessagesData(data);
        const screenIds = getScreenIdsFromMessages(data);
        setSelectedScreen((prev) =>
          prev && screenIds.includes(prev) ? prev : screenIds[0] ?? ''
        );
      } else {
        setMessagesData(null);
        setSelectedScreen('');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      showError('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages, selectedLanguage]);

  const screenIds = getScreenIdsFromMessages(messagesData);
  const currentMessages = getMessagesForScreen(messagesData, selectedScreen);
  const statusOptions = getStatusesFromMessages(currentMessages);
  const filteredMessages = filterMessagesByStatus(currentMessages, selectedStatus);

  const handleScreenChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedScreen(newValue);
    setEditingId(null);
    setStatusMenuAnchor(null);
  };

  const handleStatusTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedStatus(newValue);
    setEditingId(null);
    setStatusMenuAnchor(null);
    if (newValue !== 'all') {
      setCreateForm((prev) => ({ ...prev, status: newValue }));
    }
  };

  const handleEditClick = (entry: MessageEntry) => {
    setEditingId(entry.id);
    setEditForm({ message: entry.message, status: entry.status });
    setStatusMenuAnchor(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ message: '', status: 'neutral' });
    setStatusMenuAnchor(null);
  };

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>, target: 'edit' | 'create') => {
    setStatusMenuTarget(target);
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusSelect = (status: MessageStatus) => {
    if (statusMenuTarget === 'edit') {
      setEditForm((prev) => ({ ...prev, status }));
    } else {
      setCreateForm((prev) => ({ ...prev, status }));
    }
    setStatusMenuAnchor(null);
  };

  const handleUpdate = async (id: string) => {
    if (!editForm.message.trim()) {
      showError('Message text is required');
      return;
    }

    try {
      setSaving(true);
      await adminEndpoints.updateMessage(selectedScreen, id, {
        message: editForm.message.trim(),
        status: editForm.status,
      });
      setEditingId(null);
      await fetchMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      showError('Failed to update message');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.message.trim()) {
      showError('Message text is required');
      return;
    }

    try {
      setSaving(true);
      const status = selectedStatus !== 'all' ? selectedStatus : createForm.status;
      await adminEndpoints.createMessage(selectedScreen, {
        message: createForm.message.trim(),
        status,
      });
      setCreateForm({ message: '', status: selectedStatus !== 'all' ? selectedStatus : 'neutral' });
      await fetchMessages();
    } catch (error) {
      console.error('Error creating message:', error);
      showError('Failed to create message');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      setSaving(true);
      await adminEndpoints.deleteMessage(selectedScreen, id);
      if (editingId === id) {
        handleCancelEdit();
      }
      await fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      showError('Failed to delete message');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderStatusButton = (
    status: string,
    target: 'edit' | 'create',
    disabled: boolean
  ) => (
    <Button
      variant="text"
      onClick={(e) => handleStatusMenuOpen(e, target)}
      disabled={disabled}
      endIcon={<KeyboardArrowDownIcon />}
      sx={{ textTransform: 'none', minWidth: 100 }}
    >
      {status}
    </Button>
  );

  const renderMessageRow = (entry: MessageEntry, index: number) => {
    const isEditing = editingId === entry.id;

    return (
      <Box
        key={entry.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {isEditing ? (
          <TextField
            value={editForm.message}
            onChange={(e) => setEditForm((prev) => ({ ...prev, message: e.target.value }))}
            size="small"
            fullWidth
            disabled={saving}
            autoFocus
          />
        ) : (
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {entry.message}
          </Typography>
        )}

        {isEditing ? (
          renderStatusButton(editForm.status, 'edit', saving)
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 100, textAlign: 'center' }}
          >
            {entry.status}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {isEditing ? (
            <>
              <Tooltip title="Done">
                <IconButton
                  size="small"
                  onClick={() => handleUpdate(entry.id)}
                  disabled={saving}
                  sx={{
                    border: '1px solid',
                    borderColor: 'success.main',
                    color: 'success.main',
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  sx={{ fontSize: '0.75rem', px: 1 }}
                >
                  ✕
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(entry)}
                  disabled={saving || editingId !== null}
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(entry.id)}
                  disabled={saving || editingId !== null}
                  sx={{
                    border: '1px solid',
                    borderColor: 'error.main',
                    color: 'error.main',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const renderCreateRow = () => {
    const statusLocked = selectedStatus !== 'all';

    return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        backgroundColor: 'rgba(25, 118, 210, 0.04)',
        borderTop: '2px solid',
        borderColor: 'primary.main',
      }}
    >
      <TextField
        value={createForm.message}
        onChange={(e) => setCreateForm((prev) => ({ ...prev, message: e.target.value }))}
        size="small"
        fullWidth
        placeholder="New message..."
        disabled={saving || editingId !== null}
      />
      {statusLocked ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 100, textAlign: 'center' }}
        >
          {selectedStatus}
        </Typography>
      ) : (
        renderStatusButton(createForm.status, 'create', saving || editingId !== null)
      )}
      <Tooltip title="Done">
        <IconButton
          size="small"
          onClick={handleCreate}
          disabled={saving || editingId !== null || !createForm.message.trim()}
          sx={{
            border: '1px solid',
            borderColor: 'success.main',
            color: 'success.main',
            flexShrink: 0,
          }}
        >
          <CheckIcon />
        </IconButton>
      </Tooltip>
    </Box>
    );
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Manage Messages
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Language: {selectedLanguage.toUpperCase()}
        {messagesData && (
          <>
            {' · '}
            Last updated: {formatDate(messagesData.updatedAt)}
          </>
        )}
      </Typography>

      <Paper sx={{ mb: 3 }}>
        {screenIds.length > 0 && (
          <Tabs
            value={selectedScreen}
            onChange={handleScreenChange}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            {screenIds.map((screenId) => (
              <Tab
                key={screenId}
                label={formatScreenLabel(screenId)}
                value={screenId}
              />
            ))}
          </Tabs>
        )}

        {!loading && screenIds.length > 0 && statusOptions.length > 0 && (
          <Tabs
            value={selectedStatus}
            onChange={handleStatusTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              minHeight: 40,
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              '& .MuiTab-root': {
                minHeight: 40,
                py: 1,
                fontSize: '0.875rem',
                textTransform: 'none',
              },
            }}
          >
            <Tab label="All" value="all" />
            {statusOptions.map((status) => (
              <Tab key={status} label={status} value={status} />
            ))}
          </Tabs>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : screenIds.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No screens found in messages response.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }}
            >
              <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                Message
              </Typography>
              <Typography variant="subtitle2" sx={{ minWidth: 100, textAlign: 'center', fontWeight: 'bold' }}>
                Status
              </Typography>
              <Box sx={{ width: 88, flexShrink: 0 }} />
            </Box>

            {filteredMessages.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                {selectedStatus === 'all'
                  ? 'No messages for this screen yet.'
                  : `No ${selectedStatus} messages for this screen yet.`}
              </Typography>
            ) : (
              filteredMessages.map((entry, index) => renderMessageRow(entry, index))
            )}

            {renderCreateRow()}
          </>
        )}
      </Paper>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {MESSAGE_STATUSES.map((status) => (
          <MenuItem key={status} onClick={() => handleStatusSelect(status)}>
            {status}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
};

export default Messages;
